import React, { useState } from 'react';
import { Modal, Box, Button } from '@mui/material';

import { Loading, ErrorMessage, StatusMessage } from '@/src/ui';
import type { DistrictBaseDTO } from '../models/districtDTOs';

interface DeleteDistrictModalProps {
    open: boolean;
    district?: DistrictBaseDTO | null;
    onDelete: (districtId: number) => Promise<void> | void;
    onClose: (_shouldRefresh: boolean, _isInTimeout: boolean) => void;
}

const DeleteDistrictModal: React.FC<DeleteDistrictModalProps> = ({
    open,
    district,
    onDelete,
    onClose,
}) => {
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [firstConfirm, setFirstConfirm] = useState(false);

    const handleSubmit = async (e: React.FormEvent<any>) => {
        e.preventDefault();
        if (submitting || success) return;
        if (!district?.id) {
            setError('Unable to load district Id');
            return;
        }
        if (!firstConfirm) {
            setFirstConfirm(true);
            return;
        }
        setSubmitting(true);
        setError('');
        try {
            await onDelete(district.id);
            setSuccess(true);
            setTimeout(() => {
                onClose(true, false);
                setFirstConfirm(false);
                setSuccess(false);
            }, 750);
        } catch {
            setError('Failed to delete district');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal open={open} onClose={() => onClose(false, success)}>
            <Box sx={{ p: 4, bgcolor: 'background.paper', margin: 'auto', mt: 8, width: 400 }}>
                <h2>Delete District</h2>
                <p>Are you sure you want to delete this district?</p>
                <p>
                    <strong>Name:</strong> {district?.name}
                </p>
                <p>
                    <strong>Code:</strong> {district?.code}
                </p>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                    {!firstConfirm ? (
                        <>
                            <Button variant="outlined" onClick={() => onClose(false, false)}>
                                Cancel
                            </Button>
                            <Button variant="contained" color="error" onClick={handleSubmit}>
                                Delete
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                disabled={submitting || success}
                                variant="contained"
                                color="error"
                                onClick={handleSubmit}
                            >
                                Confirm Delete
                            </Button>
                            <Button
                                disabled={submitting || success}
                                variant="outlined"
                                onClick={() => onClose(false, false)}
                            >
                                Cancel
                            </Button>
                        </>
                    )}
                </Box>
                <ErrorMessage error={error} />
                <StatusMessage
                    visible={success}
                    status="District deleted successfully!"
                    style={{ marginTop: 8 }}
                />
                <Loading visible={submitting} message="Processing..." size="sm" />
            </Box>
        </Modal>
    );
};

export default DeleteDistrictModal;
